class FxSliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.items = Array.from(this.querySelectorAll('[id^="Slide-"]'));
    this.currentIndex = 0;
    this.changePerSecond = parseInt(this.dataset.changePerSecond, 10) || 4;
    this.productsToShow = parseInt(this.dataset.productToShow, 10) || 4;
    this.maxTranslatePercentage = -(this.items.length * 100 - this.productsToShow * 100);
    if (this.maxTranslatePercentage > 0) this.maxTranslatePercentage = 0;
    this.maxIndex = Math.ceil(this.items.length / this.productsToShow) - 1;
    if (this.maxIndex < 0) this.maxIndex = 0;
    this.isGeoLocation = this.dataset.geoLocation === 'true';


    this.isDragging = false;
    this.startX = 0;
    this.currentTranslate = 0;
    this.prevTranslate = 0;
    this.animationID = 0;

    this.autoRotate = false;
    this.autoRotateInterval = null;

    this.debouncedResize = this.debounce(this.handleResize.bind(this), 200);

    this.handleStartDrag = this.handleStartDrag.bind(this);
    this.handleMove = this.handleMove.bind(this);
    this.handleEndDrag = this.handleEndDrag.bind(this);
  }

  connectedCallback() {
    requestAnimationFrame(() => {
      this.createDots();
      this.addEventListeners();

      // Start auto-rotate if enabled
      if (this.dataset.autoRotate === 'true') {
        this.autoRotate = true;
        this.startAutoRotate();
      }
    });

    // Observe changes to the `data-auto-rotate` attribute
    this.observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'data-auto-rotate') {
          this.handleAutoRotateChange();
        }
      });
    });

    this.observer.observe(this, {
      attributes: true,
      attributeFilter: ['data-auto-rotate'],
    });
  }

  disconnectedCallback() {
    this.removeEventListeners();
    this.stopAutoRotate();
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  handleAutoRotateChange() {
    // Handle changes to `data-auto-rotate`
    this.autoRotate = this.dataset.autoRotate === 'true';
    if (this.autoRotate) {
      this.startAutoRotate();
    } else {
      this.stopAutoRotate();
    }
  }

  addEventListeners() {
    // Add event listeners for drag and resize
    this.slider.addEventListener('touchstart', this.handleStartDrag);
    this.slider.addEventListener('touchmove', this.handleMove);
    this.slider.addEventListener('touchend', this.handleEndDrag);

    this.slider.addEventListener('mousedown', this.handleStartDrag);
    window.addEventListener('mousemove', this.handleMove);
    window.addEventListener('mouseup', this.handleEndDrag);

    window.addEventListener('resize', this.debouncedResize);

    if (this.autoRotate) {
      this.slider.addEventListener('mouseenter', this.stopAutoRotate.bind(this));
      this.slider.addEventListener('mouseleave', this.startAutoRotate.bind(this));
    }
  }

  removeEventListeners() {
    // Remove all event listeners to prevent memory leaks
    this.slider.removeEventListener('touchstart', this.handleStartDrag);
    this.slider.removeEventListener('touchmove', this.handleMove);
    this.slider.removeEventListener('touchend', this.handleEndDrag);

    this.slider.removeEventListener('mousedown', this.handleStartDrag);
    window.removeEventListener('mousemove', this.handleMove);
    window.removeEventListener('mouseup', this.handleEndDrag);

    window.removeEventListener('resize', this.debouncedResize);

    if (this.autoRotate) {
      this.slider.removeEventListener('mouseenter', this.stopAutoRotate.bind(this));
      this.slider.removeEventListener('mouseleave', this.startAutoRotate.bind(this));
    }
  }

  hasAncestorDisplayContents(el) {
    let currentEl = el.parentElement;
    while (currentEl) {
      if (getComputedStyle(currentEl).display === 'contents') {
        return true;
      }
      currentEl = currentEl.parentElement;
    }
    return false;
  }

  handlePrev() {
    // Navigate to the previous slide
    if (this.currentIndex > 0) {
      this.currentIndex -= 1;
    }
    this.updateItemsPosition();
    this.updateDots();
    this.resetAutoRotate();
  }

  handleNext() {
    // Navigate to the next slide
    if (this.currentIndex < this.maxIndex) {
      this.currentIndex += 1;
    } else {
      this.currentIndex = 0; // Loop back to the first slide
    }
    this.updateItemsPosition();
    this.updateDots();
    this.resetAutoRotate();
  }

  getCurrentSliderTranslateX() {
    const transform = window.getComputedStyle(this.slider).transform;
    if (transform !== 'none') {
      const match = transform.match(/matrix.*\((.+)\)/);
      if (match) {
        const values = match[1].split(', ');
        return parseFloat(values[4]);
      }
    }
    return 0;
  }

  handleStartDrag(event) {
    this.isDragging = true;
    const isMobile = window.innerWidth <= 798;
    this.startX = event.touches ? event.touches[0].clientX : event.clientX;
    this.startY = event.touches ? event.touches[0].clientY : event.clientY;
    this.prevTranslate = this.getCurrentSliderTranslateX();
    this.slider.style.transition = 'none';
    if (!isMobile) {
      this.slider.style.overflow = 'visible';
    }
    this.animationID = requestAnimationFrame(this.animation.bind(this));
    this.slider.classList.add('active');
    this.stopAutoRotate();
  }

  handleMove(event) {
    if (!this.isDragging) return;

    const currentX = event.touches ? event.touches[0].clientX : event.clientX;
    const deltaX = currentX - this.startX;

    if (this.currentIndex === 0 && deltaX > 0) return;
    if (this.currentIndex === this.maxIndex && deltaX < 0) return;

    const currentY = event.touches ? event.touches[0].clientY : event.clientY;
    const deltaY = currentY - this.startY;
    if (Math.abs(deltaX) > Math.abs(deltaY)) {
      event.preventDefault();
    }

    this.currentTranslate = this.prevTranslate + deltaX;

    if (deltaX !== 0) {
      this.enableCardWrappers();
    }
  }

  handleEndDrag() {
    if (!this.isDragging) return;

    cancelAnimationFrame(this.animationID);
    this.isDragging = false;
    this.slider.classList.remove('active');

    const movedBy = this.currentTranslate - this.prevTranslate;

    if (movedBy < -50 && this.currentIndex < this.maxIndex) {
      this.currentIndex += 1;
    }
    if (movedBy > 50 && this.currentIndex > 0) {
      this.currentIndex -= 1;
    }

    this.slider.style.transition = '';
    this.slider.style.transform = '';
    this.slider.style.overflow = 'hidden';
    this.updateItemsPosition();
    this.updateDots();
    this.currentTranslate = 0;
    this.prevTranslate = 0;

    this.disableCardWrappers();
    this.resetAutoRotate();
  }

  updateItemsPosition() {
    // Update the position of slider items
    const isMobile = window.innerWidth <= 798;
    const showProducts = isMobile ? 2 : this.productsToShow;
    const gap = isMobile ? 4 : 8;
    const itemWidth = this.items[0].offsetWidth;
    let totalWidth;

    if (this.isGeoLocation) {
      const itemsWithContents = Array.from(this.items).filter(item =>
        this.hasAncestorDisplayContents(item)
      );
      const countWithContents = itemsWithContents.length;
      if (countWithContents === 0) return;
      totalWidth = countWithContents * (itemWidth + gap);
    }
    else {
      if (this.items.length === 0) return;
      totalWidth = this.items.length * (itemWidth + gap);
    }

    const step = itemWidth * 2 + gap * 2;
    const visibleWidth = showProducts * (itemWidth + gap);
    let maxTranslateX = totalWidth - visibleWidth;

    if (maxTranslateX < 0) maxTranslateX = 0;

    this.maxIndex = Math.ceil(maxTranslateX / step);

    let translateX = this.currentIndex * step;

    if (translateX > maxTranslateX) {
      translateX = maxTranslateX;
      this.currentIndex = this.maxIndex;
    }

    this.items.forEach((item) => {
      item.style.transform = `translateX(-${translateX}px)`;
    });
  }

  createDots() {
    // Create navigation dots
    const dotsContainer = this.querySelector('.dots');
    const isMobile = window.innerWidth <= 798;
    const showProducts = isMobile ? 2 : this.productsToShow;
    const scrollStep = 2;
    let totalDots = 0;
    dotsContainer.innerHTML = '';

    if (!dotsContainer) {
      console.warn('Dots container not found, retrying in next tick');
      requestAnimationFrame(() => this.createDots());
      return;
    }

    if (this.isGeoLocation) {
      const itemsWithContents = Array.from(this.items).filter(item =>
        this.hasAncestorDisplayContents(item)
      );

      const countWithContents = itemsWithContents.length;
      if (countWithContents === 0) {
        return;
      }
      
      if (countWithContents > showProducts) {
        totalDots = Math.ceil((countWithContents - showProducts) / scrollStep) + 1;
      }

      const fragment = document.createDocumentFragment();

      for (let i = 0; i < totalDots; i++) {
        const dot = document.createElement('span');
        dot.classList.add('dot');
        if (i === this.currentIndex) {
          dot.classList.add('active');
        }
        fragment.appendChild(dot);
      }

      dotsContainer.appendChild(fragment);
    }
    else {
      if (this.items.length > showProducts) {
        totalDots = Math.ceil((this.items.length - showProducts) / scrollStep) + 1;
      }

      for (let i = 0; i < totalDots; i++) {
        const dot = document.createElement('span');
        dot.classList.add('dot');
        if (i === this.currentIndex) {
          dot.classList.add('active');
        }
        dotsContainer.appendChild(dot);
      }
    }
  }

  updateDots() {
    // Update active state of dots
    const dots = this.querySelectorAll('.dots .dot');
    dots.forEach((dot, index) => {
      dot.classList.toggle('active', index === this.currentIndex);
    });
  }

  enableCardWrappers() {
    // Temporarily disable interaction with card wrappers
    this.items.forEach((item) => {
      const cardWrapper = item.querySelector('.card-wrapper');
      if (cardWrapper) cardWrapper.classList.add('disabled-element');
    });
  }

  disableCardWrappers() {
    // Re-enable interaction with card wrappers
    this.items.forEach((item) => {
      const cardWrapper = item.querySelector('.card-wrapper');
      if (cardWrapper) cardWrapper.classList.remove('disabled-element');
    });
  }

  animation() {
    if (this.isDragging) {
      this.slider.style.transform = `translateX(${this.currentTranslate}px)`;
      requestAnimationFrame(this.animation.bind(this));
    }
  }

  startAutoRotate() {
    // Start auto-rotation
    if (this.autoRotateInterval) return;
    this.autoRotateInterval = setInterval(() => {
      this.handleNext();
    }, this.changePerSecond * 1000);
  }

  stopAutoRotate() {
    // Stop auto-rotation
    if (this.autoRotateInterval) {
      clearInterval(this.autoRotateInterval);
      this.autoRotateInterval = null;
    }
  }

  resetAutoRotate() {
    // Reset auto-rotation timer
    if (this.autoRotate) {
      this.stopAutoRotate();
      this.startAutoRotate();
    }
  }

  debounce(func, delay) {
    // Utility function to debounce events
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        func.apply(this, args);
      }, delay);
    };
  }

  handleResize() {
    // Handle window resize events
    this.updateItemsPosition();
    this.createDots();
  }
}

customElements.define('fx-slider-component', FxSliderComponent); 